/**
 * Client-side photo compression. Re-encodes a captured image through a canvas (which also strips
 * EXIF, including any GPS tags) and downscales + lowers JPEG quality until the result fits under a
 * target byte budget. The compressed photo is uploaded to /api/intake/photo as a base64 data URL.
 */

export interface CompressedPhoto {
  /** data:image/jpeg;base64,... ready to POST. */
  dataUrl: string;
  bytes: number;
  width: number;
  height: number;
}

const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_MAX_BYTES = 2 * 1024 * 1024; // mirror the API ceiling
const MIN_QUALITY = 0.4;

function loadBitmap(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function approxBytesOfDataUrl(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  // base64 expands by 4/3; the encoded length maps back to ~ (len * 3 / 4) bytes.
  return Math.floor((b64.length * 3) / 4);
}

/** Compress a captured image File/Blob to a JPEG data URL under maxBytes. */
export async function compressPhoto(
  file: Blob,
  opts: { maxDimension?: number; maxBytes?: number } = {},
): Promise<CompressedPhoto> {
  const maxDimension = opts.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const maxBytes = opts.maxBytes ?? DEFAULT_MAX_BYTES;

  const img = await loadBitmap(file);
  const scale = Math.min(1, maxDimension / Math.max(img.naturalWidth, img.naturalHeight || 1));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, width, height);

  let quality = 0.82;
  let dataUrl = canvas.toDataURL('image/jpeg', quality);
  while (approxBytesOfDataUrl(dataUrl) > maxBytes && quality > MIN_QUALITY) {
    quality = Math.max(MIN_QUALITY, quality - 0.12);
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  }

  return { dataUrl, bytes: approxBytesOfDataUrl(dataUrl), width, height };
}
